/*
Purpose: Every `API/api_index.php` call the Calendar (v2) screen makes.
Project: TCMS

Each function documents the backend `mode` it targets and returns the already-unwrapped
payload, so components and hooks never build request bodies by hand. Payload key names
are dictated by the existing PHP endpoints and are intentionally left in snake_case.
*/

import axios from 'axios';
// moment-timezone rather than plain moment: `reEnrolStudent` stamps `currentDate` in the
// centre's timezone, which needs the `.tz()` extension.
import Moment from 'moment-timezone';
import { BASE_URL, security_key } from '../../global';
import { apiClient } from '../../infra/api-client';

const ENDPOINT = `${BASE_URL}API/api_index.php`;

/** The signed-in centre key, used as `fk_cent_key` and as the audit user on writes. */
function centreKey() {
  return sessionStorage.getItem('key');
}

/**
 * POST a request and hand back `response.data`.
 *
 * Network and HTTP failures reject, so callers can rely on `try/catch` rather than
 * inspecting a mixture of transport and application errors.
 */
async function post(payload, { client = axios } = {}) {
  const response = await client.post(ENDPOINT, {
    security_key,
    ...payload,
  });
  return response.data;
}

/** `data.success === true` plus a payload — the shape most read endpoints use. */
function readList(data) {
  return data?.success === true && Array.isArray(data.data) ? data.data : [];
}

/**
 * Several write endpoints report success as `data.data === 1` while ignoring
 * `data.success`, and some return the number as a string.
 */
function isAffirmative(data) {
  return parseInt(data?.data, 10) === 1;
}

/* ------------------------------------------------------------------ */
/* Reads                                                              */
/* ------------------------------------------------------------------ */

/**
 * Sessions for a date window, including per-student defer state.
 *
 * `Calstartday` / `Calendday` are the legacy custom-date-range fields used by the old
 * FullCalendar screen. They are optional: the PHP uses them only when both are non-empty
 * valid dates, and falls back to the `startday` / `endday` week window otherwise.
 * Sending an empty string is the safe "no custom range" signal; we never send
 * "Invalid date" (what `moment(null).format()` produces) as that causes a PDO crash.
 *
 * @param {Object} params
 * @param {string} params.startDate Inclusive `YYYY-MM-DD` window start.
 * @param {string} params.endDate   Inclusive `YYYY-MM-DD` window end.
 * @param {string} params.hoursFilter One of `HOURS_FILTER` — the backend's `checkOP`.
 */
export async function fetchSessions({ startDate, endDate, hoursFilter }) {
  const data = await post({
    mode: 'getAllSessionFromTimeCal_defer',
    fk_cent_key: centreKey(),
    checkOP: hoursFilter,
    startday: startDate,
    endday: endDate,
    Calstartday: startDate,
    Calendday: endDate,
  });

  return readList(data);
}

/** Centre operating hours for the current date; drives the grid's hour columns. */
export async function fetchOperatingHours() {
  const data = await post({
    mode: 'getCurrDateOPH',
    fk_cent_key: centreKey(),
  });

  return data?.success === true ? data.data : null;
}

/** Parent-submitted defer requests awaiting an admin decision. */
export async function fetchDeferRequests() {
  const data = await post({
    mode: 'getAllCSDeferRequest',
    fk_cent_key: centreKey(),
  });

  return readList(data);
}

/** Defer requests an admin has already rejected; read-only history. */
export async function fetchDisapprovedDeferRequests() {
  const data = await post({
    mode: 'getAllCSDisapprovedDeferRequest',
    fk_cent_key: centreKey(),
  });

  return readList(data);
}

/** Approved deferrals awaiting Forfeit / Writeoff / Void or a make-up slot. */
export async function fetchDeferBin() {
  const data = await post({
    mode: 'getAllCSDeferBin',
    fk_cent_key: centreKey(),
  });

  return readList(data);
}

/** Parent-submitted one-off session requests. */
export async function fetchOneOffRequests() {
  const data = await post({
    mode: 'getAllOneOffRequest',
    fk_cent_key: centreKey(),
  });

  return readList(data);
}

/** Centre notes shown in the filter panel. */
export async function fetchNotes() {
  const data = await post({
    mode: 'getAllNotes',
    fk_cent_key: centreKey(),
  });

  return readList(data);
}

/**
 * Tutors bookable at this centre, as `react-select` options.
 *
 * @param {string|null} includeTutorKey Keeps an inactive tutor in the list when they
 *   are already assigned to the session being edited, so the required field is never
 *   blank.
 */
export async function fetchTutorOptions(includeTutorKey = null) {
  const data = await post({
    mode: 'getAllTutorbycenterSess',
    center_key: centreKey(),
    include_tut_key: includeTutorKey,
  });

  const tutors = Array.isArray(data?.data) ? data.data : [];

  return tutors.map((tutor) => ({
    value: tutor.pk_tut_key,
    label: `${tutor.tut_fname} ${tutor.tut_surname}${
      parseInt(tutor.ta_visibility, 10) === 1 ? '' : ' (inactive)'
    }`,
  }));
}

/* ------------------------------------------------------------------ */
/* Per-student writes                                                 */
/* ------------------------------------------------------------------ */

/**
 * Overwrite a student's attendance status.
 *
 * @param {Object} params
 * @param {string} params.attendanceKey `attendance_key` from the student row.
 * @param {number} params.status One of `EDITABLE_ATTENDANCE_STATUSES`.
 */
export async function updateAttendanceStatus({ attendanceKey, status }) {
  const data = await post({
    mode: 'EditAttendanceStatus',
    fk_cent_key: centreKey(),
    attendance_key: attendanceKey,
    status,
    updated_by: centreKey(),
  });

  return data?.success === true && isAffirmative(data);
}

/**
 * Switch a student between In Centre and Online for one session.
 *
 * `enrolCategory` is sent as `null` when unchanged, which is how the endpoint clears
 * a previous per-session override.
 */
export async function updateEnrolCategory({ studentKey, sessionKey, attendanceKey, enrolCategory }) {
  const data = await post({
    mode: 'EditEnrolCategory',
    enrolCategory,
    studentKey,
    sessionKey,
    attendanceKey,
  });

  return data?.success === true && isAffirmative(data);
}

/** Raise a defer (cancel-session) request for one student. */
export async function deferStudentSession({ studentKey, sessionTemplateKey, sessionKey, enrolKey }) {
  const data = await post({
    mode: 'createCancelSessionTCMS',
    fk_cent_key: centreKey(),
    stuky: studentKey,
    sestky: sessionTemplateKey,
    sesky: sessionKey,
    pk_enrol_key: enrolKey,
  });

  return isAffirmative(data);
}

/** Raise a one-off session request for one student. */
export async function createOneOffSession({
  studentKey,
  sessionTemplateKey,
  sessionKey,
  enrolKey,
}) {
  const data = await post({
    mode: 'createOneOffSession',
    fk_cent_key: centreKey(),
    stuky: studentKey,
    sestky: sessionTemplateKey,
    sesky: sessionKey,
    pk_enrol_key: enrolKey,
  });

  return isAffirmative(data);
}

/**
 * Toggle flexi for one student on one session.
 *
 * @param {Object} params
 * @param {number} params.isFlexi `1` to enable, `0` to disable.
 * @returns {Promise<{ ok: boolean, notFound: boolean }>}
 */
export async function setFlexiSession({ sessionKey, studentKey, isFlexi }) {
  const data = await post({
    mode: 'setFlexiSession',
    fk_cent_key: centreKey(),
    pk_ses_key: sessionKey,
    pk_student_key: studentKey,
    is_flexi: isFlexi,
  });

  return {
    ok: data?.success === true && isAffirmative(data),
    notFound: data?.data === 'RECORD_NOT_FOUND',
  };
}

/**
 * Sessions a deferred student can be re-enrolled into.
 *
 * Returns an empty array when nothing is available, which the caller surfaces as an
 * empty state rather than an error.
 */
export async function fetchReEnrolSessions({ studentKey, reEnrolDate, sessionTemplateKey }) {
  const data = await post({
    mode: 'getAllSessionTimeForReEnroled',
    fk_cent_key: centreKey(),
    fk_student_key: studentKey || '',
    re_enrol_date: reEnrolDate || '',
    fk_sest_key: sessionTemplateKey || '',
  });

  return Array.isArray(data?.data) ? data.data : [];
}

/** Upcoming dates for a session template, as `react-select` options. */
export async function fetchReEnrolSessionDates(sessionTemplateKey) {
  const data = await post({
    mode: 'getSessionDatesForReEnroled',
    fk_cent_key: centreKey(),
    pk_sest_key: sessionTemplateKey,
  });

  const dates = Array.isArray(data?.data) ? data.data : [];

  return dates.map((entry) => ({
    value: entry.ses_date,
    label: Moment(entry.ses_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
  }));
}

/**
 * Move a deferred student onto a new session template and date.
 *
 * `currentDate` is stamped in the centre's timezone because the endpoint uses it to
 * decide whether the move affects an already-billed period.
 */
export async function reEnrolStudent({
  enrolKey,
  sessionTemplateKey,
  studentKey,
  targetSessionTemplateKey,
  sessionDate,
  reEnrolDate,
}) {
  const timezone = sessionStorage.getItem('timezone_identifier') || 'UTC';

  const data = await post({
    mode: 'ReEnrolStudentManagePayment',
    fk_cent_key: centreKey(),
    pk_enrol_key: enrolKey,
    fk_sest_key: sessionTemplateKey,
    fk_stu_key: studentKey,
    reenrol_fk_sest_key: targetSessionTemplateKey,
    ses_date: sessionDate,
    currentDate: Moment().tz(timezone).format('YYYY-MM-DD'),
    reenroll_date: reEnrolDate,
  });

  return isAffirmative(data);
}

/* ------------------------------------------------------------------ */
/* Inbox writes                                                       */
/* ------------------------------------------------------------------ */

/** Approve a parent's defer request, which moves it into the deferred bin. */
export async function approveDeferRequest({ cancelSessionKey, studentKey, sessionKey }) {
  const data = await post({
    mode: 'ApproveDeferRequest',
    fk_cent_key: centreKey(),
    cancel_session_key: cancelSessionKey,
    fk_student_key: studentKey,
    fk_ses_key: sessionKey,
  });

  return isAffirmative(data);
}

/** Reject a parent's defer request; the lesson stays on the calendar. */
export async function disapproveDeferRequest({ cancelSessionKey, studentKey, sessionKey }) {
  const data = await post({
    mode: 'DisApproveDeferRequest',
    fk_cent_key: centreKey(),
    cancel_session_key: cancelSessionKey,
    fk_student_key: studentKey,
    fk_ses_key: sessionKey,
  });

  return isAffirmative(data);
}

/**
 * Settle a deferred lesson as forfeited or written off.
 *
 * @param {Object} params
 * @param {Object} params.record A row from `fetchDeferBin`.
 * @param {number} params.forfeitType One of `FORFEIT_TYPE`.
 */
export async function settleDeferredSession({ record, forfeitType }) {
  const data = await post({
    mode: 'changeStudentEnrolmentForfeitWriteoff',
    fk_cent_key: centreKey(),
    pk_enrol_key: record.pk_enrol_key,
    fw_session_date: record.sDate,
    fw_session_time: record.sTime,
    stuky: record.fk_student_key,
    sestky: record.fk_sest_key,
    fk_ses_key: record.fk_ses_key,
    fw_type: forfeitType,
  });

  return data;
}

/**
 * Void a deferral, returning the lesson to the student's balance.
 *
 * Uses `apiClient` because this endpoint is behind bearer-token auth, unlike the
 * older modes above.
 *
 * @param {Object} record A row from `fetchDeferBin`, forwarded whole.
 * @returns {Promise<{ ok: boolean, reason: string|null }>}
 */
export async function voidDeferment(record) {
  const data = await post({ ...record, mode: 'voidDeferment' }, { client: apiClient });

  if (data?.data === 1) {
    return { ok: true, reason: null };
  }

  return {
    ok: false,
    reason: typeof data?.data === 'string' ? data.data : null,
  };
}

/**
 * Assign selected deferred / one-off rows onto a target session.
 *
 * @param {Object} params
 * @param {Object} params.session The session view model receiving the students.
 * @param {Object[]} params.records Rows selected from the deferred bin or one-off inbox.
 */
export async function assignSessionsToSlot({ session, records }) {
  const data = await post({
    mode: 'ResignSessionArray',
    fk_cent_key: centreKey(),
    pk_ses_key: session.sessionKey,
    fk_sest_key: session.sessionTemplateKey,
    fk_tut_key: session.tutorKey,
    ses_date: session.date,
    sest_start_time: session.startTime,
    data: records,
  });

  return isAffirmative(data);
}

/* ------------------------------------------------------------------ */
/* Session and note writes                                            */
/* ------------------------------------------------------------------ */

/** Reschedule a session; the endpoint rejects clashes on the tutor's diary. */
export async function updateSessionDateTime({
  sessionKey,
  tutorKey,
  sessionDate,
  startTime,
  endTime,
  duration,
}) {
  const data = await post({
    mode: 'updateSessionDateTime_new',
    pk_ses_key: sessionKey,
    fk_tut_key: tutorKey,
    ses_date: sessionDate,
    ses_start_time: startTime,
    ses_end_time: endTime,
    ses_duration: duration,
    updated_by: centreKey(),
  });

  if (data?.success !== true) {
    return { ok: false, tutorUnavailable: false };
  }

  const outcome = parseInt(data.data, 10);
  return {
    ok: outcome === 1,
    // The endpoint answers `2` when the tutor already has a session at that time.
    tutorUnavailable: outcome === 2,
  };
}

/** Add a centre note. */
export async function createNote(noteText) {
  const data = await post({
    mode: 'createNotes',
    nt_notes: noteText,
    fk_cent_key: centreKey(),
    created_by: centreKey(),
    visibility: 1,
  });

  return data?.success === true && isAffirmative(data);
}

/** Delete a centre note. */
export async function deleteNote(noteKey) {
  const data = await post({
    mode: 'deleteNotes',
    fk_cent_key: centreKey(),
    noteskey: noteKey,
  });

  return data;
}

/* ------------------------------------------------------------------ */
/* Create session time / Add enrolment                                */
/* ------------------------------------------------------------------ */

/**
 * Create a recurring (or one-off) session time template.
 *
 * @returns {Promise<{ ok: boolean, code: number|null, message: string|null }>}
 */
export async function createSessionTime(formValues) {
  const data = await post({
    mode: 'createSessionTime',
    ...formValues,
    fk_cent_key: centreKey(),
    created_by: centreKey(),
  });

  if (data?.success === true && parseInt(data.data, 10) === 1) {
    return { ok: true, code: 1, message: null };
  }

  const code = parseInt(data?.data, 10);
  const messages = {
    2: 'This date, time, or day has already been assigned to the tutor. Please choose a different date, time, or day.',
    3: 'The desired day does not exist within the date range.',
    4: 'Unable to generate session. Please choose a different date or check holiday sessions.',
  };

  return {
    ok: false,
    code: Number.isNaN(code) ? null : code,
    message: messages[code] || null,
  };
}

/** Active students at this centre, as select options. */
export async function fetchStudentOptions() {
  const data = await post({
    mode: 'getAllStudentWithCard',
    fk_cent_key: centreKey(),
  });

  const students = readList(data);

  return students.map((student) => ({
    value: student.pk_student_key,
    label: `${student.st_first_name || ''} ${student.st_surname || ''}`.trim(),
    raw: student,
  }));
}

/** Subjects available for a student at this centre. */
export async function fetchSubjectOptions(studentKey) {
  const data = await post({
    mode: 'getAllSubjectby_center',
    fk_student_key: studentKey,
    fk_cent_key: centreKey(),
  });

  const subjects = Array.isArray(data?.data) ? data.data : [];

  return subjects.map((subject) => ({
    value: subject.subject_id,
    label: subject.subject_name,
  }));
}

/**
 * Session times a student can enrol into.
 *
 * @param {Object} params
 * @param {string} params.studentKey
 * @param {string} [params.sessionType] Online / In centre filter from category.
 * @param {string} [params.productTypeKey]
 * @param {string|boolean} [params.classId]
 */
export async function fetchEnrolmentSessionOptions({
  studentKey,
  sessionType = '',
  productTypeKey = '',
  classId = false,
}) {
  const data = await post({
    mode: 'getAllSessionTimeByStudent',
    fk_student_key: studentKey,
    fk_cent_key: centreKey(),
    sest_type: sessionType,
    fk_pt_key: productTypeKey,
    class_id: classId,
  });

  const sessions = Array.isArray(data?.data) ? data.data : [];

  return [
    { value: '', label: '--Select--' },
    ...sessions.map((session) => ({
      value: session.pk_sest_key,
      label: session.sest_name,
    })),
  ];
}

/** Product types offered for student enrolment. */
export async function fetchProductTypeOptions() {
  const data = await post({
    mode: 'getProductTypeStuEnrol',
  });

  const types = Array.isArray(data?.data) ? data.data : [];

  return types.map((type) => ({
    value: type.pk_pt_key,
    label: type.pt_name,
  }));
}

/** Products for a payment method + product type. */
export async function fetchProductOptions({ paymentMethod, productTypeKey, studentKey }) {
  const data = await post({
    mode: 'getAllProductOnlineOfflineCentre',
    pp_pay_method: paymentMethod,
    fk_pt_key: productTypeKey,
    fk_student_key: studentKey,
    fk_cent_key: centreKey(),
  });

  const products = Array.isArray(data?.data) ? data.data : [];

  return products.map((product) => ({
    value: product.pk_pp_key,
    label: `${product.pp_name}(${product.pp_amount})`,
  }));
}

/**
 * Whether Online payment is available for this student, and the verification row to
 * spread into the create payload.
 *
 * @returns {Promise<{ paymentMethodOptions: Object[], verifyData: Object|null }>}
 */
export async function fetchStudentPaymentVerify(studentKey) {
  const data = await post({
    mode: 'getCenterStudentVerifiedDetails',
    fk_cent_key: centreKey(),
    fk_student_key: studentKey,
  });

  const rows = Array.isArray(data?.data) ? data.data : [];
  const hasOnline = rows.length > 0;

  return {
    paymentMethodOptions: hasOnline
      ? [
          { value: '', label: '---Select---' },
          { value: '1', label: 'Online' },
          { value: '2', label: 'Offline' },
        ]
      : [
          { value: '', label: '---Select---' },
          { value: '2', label: 'Offline' },
        ],
    verifyData: hasOnline ? rows[0] : null,
  };
}

/**
 * Create a student pause covering a date range for all sessions or one template.
 *
 * @returns {Promise<{ ok: boolean, code: number|null, message: string|null }>}
 */
export async function createStudentPause({
  studentKey,
  enrolKey,
  sessionTemplateKey,
  pauseStartDate,
  pauseEndDate,
  pauseDescription,
  pauseApplyType,
}) {
  const data = await post({
    mode: 'createStudentPause',
    fk_cent_key: centreKey(),
    fk_stu_key: studentKey,
    pk_enrol_key: enrolKey,
    fk_sest_key: sessionTemplateKey,
    pause_start_date: pauseStartDate,
    pause_end_date: pauseEndDate,
    pause_description: pauseDescription,
    pause_apply_type: pauseApplyType,
    created_by: centreKey(),
  });

  if (data?.success === true && parseInt(data.data, 10) === 1) {
    return { ok: true, code: 1, message: null };
  }

  const code = parseInt(data?.data, 10);
  if (code === 2) {
    return {
      ok: false,
      code: 2,
      message: 'A pause already exists for this student in the selected date range.',
    };
  }

  return {
    ok: false,
    code: Number.isNaN(code) ? null : code,
    message: null,
  };
}

/**
 * Create a student enrolment, optionally with payment-method product wiring.
 *
 * @returns {Promise<{ ok: boolean, code: number|null, message: string|null }>}
 */
export async function createStudentEnrolment(formValues, verifyData = null) {
  const data = await post({
    mode: 'createStudentEnrolmentWithPaymentMethod',
    ...formValues,
    ...(verifyData || {}),
    fk_cent_key: centreKey(),
    created_by: centreKey(),
  });

  if (data?.success === true && parseInt(data.data, 10) === 1) {
    return { ok: true, code: 1, message: null };
  }

  const code = parseInt(data?.data, 10);
  const messages = {
    2: 'Sorry! The session was deleted. Please try another session.',
    3: 'Sorry! Generate session is not available after the enrolment date. Try another session or date.',
    4: 'This student is already enrolled in this session.',
  };

  return {
    ok: false,
    code: Number.isNaN(code) ? null : code,
    message: messages[code] || null,
  };
}
